import React from 'react';
import { Box, Typography } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';

import { TemplateField, DragData, TemplateSection, Template } from '../../_types';
import { isSectionLockedByParent } from '../../_utils/sectionComparison';
import { useTemplateBuilderContext } from '../context';

interface InsertableDropZoneProps {
  sectionId: number;
  sectionGuid: string;
  insertIndex: number;
  isVisible: boolean;
  dragData?: DragData | null;
  sectionFields?: TemplateField[];
  isLocked?: boolean;
  sections?: TemplateSection[];
  parentTemplate?: Template | null;
}

const InsertableDropZone: React.FC<InsertableDropZoneProps> = ({
  sectionId,
  sectionGuid,
  insertIndex,
  isVisible,
  dragData,
  sectionFields = [],
  isLocked = false,
  sections = [],
  parentTemplate = null,
}) => {
  // Get removable child fields from context
  const { removableChildFieldIds } = useTemplateBuilderContext();

  // Disable adjacent drop zones when dragging a field within the same section
  let isDisabled = !isVisible || isLocked;

  // Disable drop zones when dragging from a locked section to a different section
  // BUT allow if it's a removable child field
  if (
    isVisible &&
    dragData?.type === 'field-within-section' &&
    dragData.sourceSectionId !== sectionId // Different section
  ) {
    const sourceSection = sections.find((s) => s.id === dragData.sourceSectionId);
    if (sourceSection && isSectionLockedByParent(sourceSection, parentTemplate)) {
      const isRemovableChildField = removableChildFieldIds.has(dragData.templateField?.id || 0);
      if (!isRemovableChildField) {
        isDisabled = true; // Only disable if NOT a removable child field
      }
    }
  }

  if (isVisible && dragData?.type === 'field-within-section' && dragData.sourceSectionId === sectionId) {
    // Find the current index of the dragging field
    const draggingFieldIndex = sectionFields.findIndex((f) => f.id === dragData.templateField?.id);

    if (draggingFieldIndex !== -1) {
      // Disable the drop zone immediately before (at draggingFieldIndex) and after (at draggingFieldIndex + 1)
      if (insertIndex === draggingFieldIndex || insertIndex === draggingFieldIndex + 1) {
        isDisabled = true;
      }
    }
  }

  const { isOver, setNodeRef } = useDroppable({
    id: `section-insert-${sectionGuid}-${insertIndex}`, // Use GUID for unique IDs
    data: {
      type: 'section-insert-zone',
      sectionId,
      sectionGuid, // Pass both for lookup
      insertIndex,
    },
    disabled: isDisabled,
  });

  if (!isVisible) return null;
  if (isDisabled) return null; // Don't render disabled drop zones

  return (
    <Box
      ref={setNodeRef}
      sx={{
        height: isOver ? 40 : 12, // Slightly more height for visibility
        mx: 1,
        borderRadius: 1,
        backgroundColor: isOver ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.02)',
        border: isOver ? '2px dashed' : '1px dashed',
        borderColor: isOver ? 'primary.main' : 'rgba(25, 118, 210, 0.2)',
        transition: 'all 0.2s ease',
        opacity: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isOver && (
        <Typography
          variant='caption'
          sx={{
            color: 'primary.main',
            fontSize: '0.75rem',
            fontWeight: 'medium',
            px: 1,
          }}
        >
          Drop here
        </Typography>
      )}
    </Box>
  );
};

export default InsertableDropZone;
